import { documentToPlainTextString } from '@contentful/rich-text-plain-text-renderer';
import styled from '@emotion/styled';
import { Entry } from 'contentful';
import React, { useCallback, useState } from 'react';
import { fieldsToProps } from '../../domains/blog/BlogArticle';
import { PostCardElement } from '../../domains/blog/PostCard';
import HorizontalList from '../../domains/blog/PostCardhorizontalList';
import { IBlogPostFields, ILandingPageFields } from '../../types/@types/generated/contentful';
import { getClient } from '../../utils/contentfulSetup';
import { colors, spacing } from '../../utils/styleguide';
import SectionBackground from '../component-library/SectionBackground';
import { PageSectionComponent } from './pageSections';
import sectionToCompatibleProps from './util/sectionToCompatibleProps';

const Wrapper = styled.div`
  background-color: ${colors.lightGray};
  padding-top: ${spacing[10]}px;
  padding-bottom: ${spacing[6]}px;
`;

type ServerSideProps = {
  articles?: PostCardElement[];
};

const BlogArticlesSection: PageSectionComponent<ServerSideProps> = ({
  section,
  serverSideProps: { articles },
}) => {
  const { id, title } = sectionToCompatibleProps({ section });

  return (
    <SectionBackground id={id}>
      <BlogArticlesSectionLayout
        articles={articles}
        title={title ? documentToPlainTextString(title) : ''}
      />
    </SectionBackground>
  );
};

const NR_OF_INITIAL_SHOWN_ROWS = 3;

const BlogArticlesSectionLayout = ({
  articles,
  title,
}: Pick<ServerSideProps, 'articles'> & { title: string }) => {
  const [maxRows, setMaxRows] = useState(NR_OF_INITIAL_SHOWN_ROWS);
  const showMore = useCallback(() => {
    setMaxRows(maxRows + NR_OF_INITIAL_SHOWN_ROWS);
  }, [maxRows]);
  return (
    <Wrapper>
      <HorizontalList
        header={title}
        items={articles || []}
        maxRows={maxRows}
        onShowMore={showMore}
      />
    </Wrapper>
  );
};

const itemsFieldsToProps = (items: Entry<IBlogPostFields>[]) => {
  return items.map((item) => {
    return fieldsToProps(item) as PostCardElement;
  });
};

BlogArticlesSection.getCustomServerSideProps = async () => {
  const blogPostResponse = await getClient().getEntries<IBlogPostFields>({
    content_type: 'blogPost',
  });
  const pages = itemsFieldsToProps(blogPostResponse.items);

  const blogLandingPageResponse = await getClient().getEntries<ILandingPageFields>({
    content_type: 'landingPage',
    'fields.tags[in]': 'Blog',
  });
  const oldPages = blogLandingPageResponse.items.map((landingPageItem) => {
    return {
      title: landingPageItem.fields.metaHeader?.fields.socialTitle || 'Milkywire blog post',
      preamble: landingPageItem.fields.metaHeader?.fields.socialDescription,
      createdAt: landingPageItem.sys.createdAt,
      slug: landingPageItem.fields.slug,
      image: landingPageItem.fields.metaHeader?.fields.socialImage,
    } as PostCardElement;
  });

  const allPages = pages.concat(oldPages);

  const articles = allPages
    .sort((item1: PostCardElement, item2: PostCardElement) => {
      const date1 = new Date(item1.createdAt);
      const date2 = new Date(item2.createdAt);
      return date1 > date2 ? 1 : 0;
    })
    .map((blogPost: PostCardElement) => {
      blogPost.createdAt = blogPost.createdAt.split('T')[0];
      return blogPost;
    });

  return { articles };
};

export default BlogArticlesSection;
